import React, { useState } from 'react';
import {
  ProductEstimate,
  getConfidenceColor,
  getConfidenceLabel,
  formatTime
} from '../../../utils/productionEstimates';
import './ProductionEstimateCard.css';

interface ProductionEstimateCardProps {
  estimate: ProductEstimate;
}

const PEAK_ICONS: Record<string, string> = {
  peak: '🔥',
  'pre-peak': '🟡',
  valley: '⚠️',
  'very-low': '🚫',
  normal: '📊',
};

const ProductionEstimateCard: React.FC<ProductionEstimateCardProps> = ({ estimate }) => {
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const lowerBound = estimate.lower_bound || estimate.prediction_range?.pessimistic || estimate.prediction_details?.lower_bound || 0;
  const estimatedQuantity = estimate.estimated_quantity || estimate.prediction_range?.most_likely || 0;
  const upperBound = estimate.upper_bound || estimate.prediction_range?.optimistic || estimate.prediction_details?.upper_bound || 0;
  const cookingTime = estimate.cooking_time || estimate.cooking_time_minutes || 0;
  const unit = estimate.unit || 'piezas';

  const isNoPrep = estimatedQuantity === 0;
  const hasRangeData = lowerBound > 0 || estimatedQuantity > 0 || upperBound > 0;

  const isPeak = estimate.is_peak ?? false;
  const peakType = estimate.peak_type ?? 'normal';
  const peakIcon = PEAK_ICONS[peakType] ?? '📊';

  const range = upperBound - lowerBound;
  const mostLikelyPos = range > 0 ? ((estimatedQuantity - lowerBound) / range) * 100 : 50;

  const handleStartProduction = () => {
    console.log(`Iniciando producción anticipada de ${estimate.product_name}`);
    alert(`Producción anticipada iniciada para ${estimate.product_name}`);
  };

  return (
    <div className={`production-estimate-card${isNoPrep ? ' no-prep' : ''}`}>
      {/* Header con título y badge */}
      <div className="card-header">
        <h3 className="product-name">🔥 {estimate.product_name}</h3>
        {isNoPrep ? (
          <span className="confidence-badge no-demand">Sin demanda estimada</span>
        ) : estimate.confidence_level != null && (
          <span
            className="confidence-badge"
            style={{ backgroundColor: getConfidenceColor(estimate.confidence_level) }}
          >
            CONFIANZA {getConfidenceLabel(estimate.confidence_level)}
          </span>
        )}
      </div>

      {/* Recomendación del sistema */}
      {!isNoPrep && estimatedQuantity > 0 && (
        <div className="recommendation-section primary">
          <p className="section-label">💡 Recomendación del sistema</p>
          <p className="recommendation-value-main">
            Prepara {estimatedQuantity} {unit}
          </p>
          {estimate.explanation && (
            <p className="explanation-text">{estimate.explanation}</p>
          )}
        </div>
      )}

      {/* Hora de inicio */}
      {!isNoPrep && estimate.suggested_start_time && (
        <div className="timing-section">
          <p>⏰ Hora de inicio: <strong>{formatTime(estimate.suggested_start_time)}</strong>
            {estimate.minutes_until_start && (
              <span className="time-remaining"> (en {estimate.minutes_until_start} minutos)</span>
            )}
          </p>
          <p>   Tiempo de cocción: <strong>{cookingTime} min</strong></p>
        </div>
      )}

      {/* Advertencia de hora (si aplica) */}
      {estimate.peak_warning && (
        <div className={`warning-box ${peakType}`}>
          <p className="warning-message">
            {peakIcon} {estimate.peak_warning}
          </p>
        </div>
      )}

      {/* Mensaje de hora pico */}
      {isPeak && estimate.peak_message && (
        <div className="peak-hour-box">
          <p className="peak-message">
            {peakIcon} {estimate.peak_message}
          </p>
          <p className="peak-tip">Considera preparar hacia el rango optimista.</p>
        </div>
      )}

      {/* Botón principal */}
      {!isNoPrep && (
        <div className="actions-section">
          <button
            className="btn-primary"
            onClick={handleStartProduction}
          >
            🍳 Adelantar producción
          </button>
        </div>
      )}

      {/* Sección colapsable: Detalles de estimación */}
      {!isNoPrep && hasRangeData && (
        <div className="collapsible-section">
          <button
            className="collapsible-toggle"
            onClick={() => setShowMoreInfo(!showMoreInfo)}
          >
            <span>📊 Detalles de estimación</span>
            <span className="toggle-icon">{showMoreInfo ? '▲' : '▼'}</span>
          </button>

          {showMoreInfo && (
            <div className="prediction-range-section">
              <div className="range-labels">
                <div className="range-label">
                  <span className="label-text">Pesimista</span>
                  <span className="value-text">{lowerBound} {unit}</span>
                </div>
                <div className="range-label center">
                  <span className="label-text">Recomendado</span>
                  <span className="value-text">{estimatedQuantity} {unit}</span>
                </div>
                <div className="range-label right">
                  <span className="label-text">Optimista</span>
                  <span className="value-text">{upperBound} {unit}</span>
                </div>
              </div>

              <div className="range-bar-container">
                <div className="range-bar">
                  <div className="range-line"></div>
                  <div className="marker pessimistic" style={{ left: '0%' }}>▼</div>
                  <div className="marker most-likely" style={{ left: `${mostLikelyPos}%` }}>●</div>
                  <div className="marker optimistic" style={{ left: '100%' }}>▼</div>
                </div>
              </div>

              <p className="confidence-text">
                ✅ 80% seguro de vender entre {lowerBound} - {upperBound} {unit}
              </p>

              {estimate.ingredients && estimate.ingredients.length > 0 && (
                <div className="details-section">
                  <p className="section-label">📦 Ingredientes:</p>
                  <ul className="ingredients-list">
                    {estimate.ingredients.map((ingredient, idx) => (
                      <li key={idx}>
                        {ingredient.product_name}: {ingredient.quantity} {ingredient.unit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductionEstimateCard;
